import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '../contexts/AuthContext';
import { formatNumero } from '../components/ChatShared';

// Painel do gestor — foto AO VIVO da operação. Reusa a mesma lista e o mesmo
// stream (SSE) da tela de Conversas; nada de chat é duplicado aqui. "Entrar" faz
// deep-link pra /ti/atendimentos?conversa=ID, onde responder/transferir/encerrar
// já funcionam.

const TOKEN_KEY = 'siti_web_token';
const CORES = ['#57A94F', '#F5A623', '#4CB5C0', '#B07BCC', '#E5748B', '#6B9BE5', '#E0993E'];

function iniciais(nome) {
  const p = String(nome || '').trim().split(/\s+/).slice(0, 2);
  return p.map((x) => x[0]?.toUpperCase()).join('') || '#';
}
function corDoNome(nome) {
  let h = 0; const s = String(nome || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return CORES[h % CORES.length];
}
function formatEspera(ms) {
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60); const m = min % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}
function corEspera(ms) {
  const min = ms / 60000;
  if (min >= 5) return '#b43737';
  if (min >= 1) return '#F5A623';
  return '#5E7A82';
}
const esperandoDe = (c, agora) => (c.aguardando_desde ? agora - new Date(c.aguardando_desde).getTime() : null);
const contaEsperando = (arr) => arr.filter((c) => c.aguardando_desde).length;

function MetricaTopo({ label, valor, cor, hint }) {
  return (
    <div style={{ flex: '1 1 140px', minWidth: 130 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>{label}</div>
      <strong style={{ fontSize: '1.6rem', color: cor || 'var(--portal-text)', display: 'block', lineHeight: 1.1 }}>{valor}</strong>
      {hint ? <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>{hint}</div> : null}
    </div>
  );
}

// Cartão de conversa no kanban — clicável (abre a conversa). Borda esquerda
// colorida pela urgência da espera.
function CardConversa({ c, agora, onEntrar }) {
  const esp = esperandoDe(c, agora);
  const nome = c.push_name || formatNumero(c.numero) || c.jid || 'Contato';
  const corBarra = esp != null ? corEspera(esp) : 'var(--portal-border)';
  return (
    <button
      type="button"
      onClick={() => onEntrar(c.id)}
      title="Abrir conversa"
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'var(--portal-bg)', border: '1px solid var(--portal-border)',
        borderLeft: `3px solid ${corBarra}`, borderRadius: 8, padding: '9px 11px',
        color: 'var(--portal-text)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
          background: corDoNome(nome), color: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontWeight: 600, fontSize: '0.72rem',
        }}>
          {c.profile_pic_url ? <img src={c.profile_pic_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : iniciais(nome)}
        </div>
        <strong style={{ flex: 1, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</strong>
        {c.nao_lidas > 0 && (
          <span style={{ background: '#25D366', color: '#fff', borderRadius: 10, fontSize: '0.66rem', fontWeight: 700, padding: '0 6px', minWidth: 18, textAlign: 'center', flexShrink: 0 }}>{c.nao_lidas}</span>
        )}
      </div>
      <div style={{ fontSize: '0.76rem', color: 'var(--portal-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 5 }}>
        {c.ultima_preview || '(sem mensagens)'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {esp != null && (
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: corEspera(esp), display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <i className="bi bi-clock-history" /> {formatEspera(esp)}
          </span>
        )}
        {c.atendente ? (
          <span style={{ fontSize: '0.7rem', color: '#3D8B37', display: 'inline-flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
            <i className="bi bi-person-check" /> {c.atendente}
          </span>
        ) : (
          <span style={{ fontSize: '0.7rem', color: '#b43737', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <i className="bi bi-person-dash" /> sem atendente
          </span>
        )}
      </div>
    </button>
  );
}

export default function Painel() {
  const { temPermissao } = useAuth();
  const podeVer = temPermissao('ti:dashboard', 'admin:atendimento');
  const navigate = useNavigate();

  const [conversas, setConversas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agora, setAgora] = useState(Date.now());
  const [agrupamento, setAgrupamento] = useState('setor'); // 'setor' | 'atendente'
  // Filial e canal são dois níveis do mesmo recorte: a loja pode ter mais de um
  // número (caixa/crediário hoje, vendas amanhã). Escolher a filial estreita a
  // lista de canais; escolher o canal manda o filtro pro backend.
  const [filiais, setFiliais] = useState([]);
  const [canais, setCanais] = useState([]);
  const [filialFiltro, setFilialFiltro] = useState('');
  const [canalFiltro, setCanalFiltro] = useState('');
  const debounceRef = useRef(null);
  const carregarRef = useRef(() => {});

  // Canais visíveis pra mim e as filiais que eles cobrem.
  useEffect(() => {
    if (!podeVer) return;
    axios.get(`${API}/atendimento/canais`).then((r) => setCanais(r.data?.data || [])).catch(() => {});
    axios.get(`${API}/atendimento/filiais`).then((r) => setFiliais(r.data?.data || [])).catch(() => {});
  }, [podeVer]);

  // Canais da filial escolhida (ou todos). É o que vai no filtro de canal e,
  // quando não há canal escolhido, o que limita a busca por filial.
  const canaisDaFilial = useMemo(
    () => (filialFiltro ? canais.filter((c) => String(c.filial) === String(filialFiltro)) : canais),
    [canais, filialFiltro],
  );

  const carregar = async () => {
    try {
      const params = { status: 'aberto' };
      if (canalFiltro) params.canal_ids = String(canalFiltro);
      else if (filialFiltro) params.canal_ids = canaisDaFilial.map((c) => c.id).join(',');
      const res = await axios.get(`${API}/atendimentos`, { params });
      setConversas(res.data?.data || []);
    } catch (_) { /* mantém o que tinha */ } finally { setLoading(false); }
  };
  carregarRef.current = carregar;

  // Trocou o recorte: recarrega. O canal escolhido some se não for da filial.
  useEffect(() => {
    if (canalFiltro && !canaisDaFilial.some((c) => String(c.id) === String(canalFiltro))) {
      setCanalFiltro('');
      return;
    }
    if (podeVer) carregar();
  }, [filialFiltro, canalFiltro, canaisDaFilial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (podeVer) carregar(); }, [podeVer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcula os tempos de espera na tela a cada 30s.
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Ao vivo: SSE (mesmo stream da tela de Conversas) + poll de segurança.
  useEffect(() => {
    if (!podeVer) return undefined;
    const poll = setInterval(() => carregarRef.current(), 12000);
    const token = localStorage.getItem(TOKEN_KEY);
    let es;
    if (token) {
      es = new EventSource(`${API}/atendimentos/stream?token=${encodeURIComponent(token)}`);
      const recarregar = () => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => carregarRef.current(), 400);
      };
      es.addEventListener('message_new', recarregar);
      es.addEventListener('conversa_update', recarregar);
      es.onerror = () => { /* o EventSource reconecta sozinho */ };
    }
    return () => { clearInterval(poll); if (es) es.close(); clearTimeout(debounceRef.current); };
  }, [podeVer]);

  const grupos = useMemo(() => {
    const porAtendente = agrupamento === 'atendente';
    const map = new Map();
    // Agrupa por ID da fila, não pelo nome: "Crediário" de Espera Feliz e
    // "Crediário" de Carangola são filas DIFERENTES, e agrupar por nome fundia
    // as duas lojas num grupo só. O rótulo mostra a loja junto quando o gestor
    // está vendo mais de um canal.
    const rotulos = new Map();
    for (const c of conversas) {
      const chave = porAtendente
        ? `a:${c.atendente || ''}`
        : `s:${c.setor_id ?? 'sem'}`;
      if (!map.has(chave)) {
        map.set(chave, []);
        rotulos.set(chave, porAtendente
          ? (c.atendente || 'Pendente (sem dono)')
          : (c.setor_nome || 'Pendente (sem fila)'));
      }
      map.get(chave).push(c);
    }
    for (const arr of map.values()) {
      // Quem espera há mais tempo primeiro (aguardando_desde mais antigo).
      arr.sort((a, b) => {
        const ea = a.aguardando_desde ? new Date(a.aguardando_desde).getTime() : Infinity;
        const eb = b.aguardando_desde ? new Date(b.aguardando_desde).getTime() : Infinity;
        return ea - eb;
      });
    }
    // Por atendente: o mais SOBRECARREGADO primeiro (mais conversas). Por fila:
    // a que tem mais gente esperando primeiro.
    return [...map.entries()]
      .map(([chave, lista]) => {
        // Sem filtro de canal, duas lojas podem aparecer juntas na tela — então
        // o nome da fila sozinho ("Crediário") não diz de quem é.
        const canaisNoGrupo = new Set(lista.map((c) => c.canal_id));
        const varias = new Set(conversas.map((c) => c.canal_id)).size > 1;
        const sufixo = (!porAtendente && varias && canaisNoGrupo.size === 1)
          ? ` · ${lista[0].canal_nome || ''}`.replace(/ · $/, '')
          : '';
        return [`${rotulos.get(chave)}${sufixo}`, lista];
      })
      .sort((a, b) => (porAtendente
        ? b[1].length - a[1].length
        : contaEsperando(b[1]) - contaEsperando(a[1])));
  }, [conversas, agrupamento]);

  const totais = useMemo(() => {
    const esperando = conversas.filter((c) => c.aguardando_desde).length;
    const semAtendente = conversas.filter((c) => !c.atendente).length;
    const maiorEspera = conversas.reduce((mx, c) => {
      const e = esperandoDe(c, agora);
      return e != null && e > mx ? e : mx;
    }, 0);
    return { total: conversas.length, esperando, semAtendente, maiorEspera };
  }, [conversas, agora]);

  const entrar = (id) => navigate(`/ti/atendimentos?conversa=${id}`);

  if (!podeVer) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Você não tem permissão para ver o painel do gestor.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Atendimento</span>
          <h1>
            Painel ao vivo
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 12, fontSize: '0.7rem', color: '#3D8B37', verticalAlign: 'middle' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3D8B37', display: 'inline-block', animation: 'pulseVivo 1.4s ease-in-out infinite' }} />
              ao vivo
            </span>
          </h1>
          <p>Visão do gestor — o que está acontecendo agora, por fila. Clique em “Entrar” para responder, transferir ou encerrar.</p>
        </div>
      </section>
      <style>{'@keyframes pulseVivo{0%,100%{opacity:1}50%{opacity:0.25}}'}</style>

      {/* Métricas do momento */}
      <section className="content-card" style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
        <MetricaTopo label="Em aberto" valor={totais.total} />
        <MetricaTopo label="Esperando resposta" valor={totais.esperando} cor={totais.esperando ? '#F5A623' : undefined} />
        <MetricaTopo label="Sem atendente" valor={totais.semAtendente} cor={totais.semAtendente ? '#b43737' : undefined} />
        <MetricaTopo label="Maior espera" valor={totais.maiorEspera ? formatEspera(totais.maiorEspera) : '—'} cor={totais.maiorEspera ? corEspera(totais.maiorEspera) : undefined} />
      </section>

      {/* Recorte: filial (loja) e canal (número). Só aparece quando há mais de
          uma opção — quem enxerga um canal só não precisa de filtro nenhum. */}
      {(filiais.length > 1 || canais.length > 1) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap',
        }}>
          {filiais.length > 1 && (
            <>
              <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Loja:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: 150 }}
                value={filialFiltro}
                onChange={(e) => setFilialFiltro(e.target.value)}
              >
                <option value="">Todas</option>
                {filiais.map((f) => <option key={f.codigo} value={f.codigo}>{f.nome}</option>)}
              </select>
            </>
          )}
          {canaisDaFilial.length > 1 && (
            <>
              <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Canal:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: 190 }}
                value={canalFiltro}
                onChange={(e) => setCanalFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                {canaisDaFilial.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </>
          )}
          {(filialFiltro || canalFiltro) && (
            <button
              type="button"
              onClick={() => { setFilialFiltro(''); setCanalFiltro(''); }}
              style={{
                fontSize: '0.76rem', padding: '4px 10px', borderRadius: 14, cursor: 'pointer',
                border: '1px solid var(--portal-border)', background: 'transparent', color: 'var(--portal-muted)',
              }}
            >
              <i className="bi bi-x-lg me-1" />Limpar
            </button>
          )}
        </div>
      )}

      {/* Agrupar por fila (padrão) ou por atendente (ver quem está sobrecarregado) */}
      {conversas.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Agrupar por:</span>
          {[{ v: 'setor', l: 'Fila', i: 'bi-inboxes' }, { v: 'atendente', l: 'Atendente', i: 'bi-person-badge' }].map((o) => {
            const ativo = agrupamento === o.v;
            return (
              <button key={o.v} type="button" onClick={() => setAgrupamento(o.v)}
                style={{
                  fontSize: '0.78rem', fontWeight: ativo ? 700 : 500, padding: '4px 12px', borderRadius: 14,
                  border: '1px solid var(--portal-border)', cursor: 'pointer',
                  background: ativo ? '#3D8B37' : 'transparent', color: ativo ? '#fff' : 'var(--portal-text)',
                }}>
                <i className={`bi ${o.i} me-1`} />{o.l}
              </button>
            );
          })}
        </div>
      )}

      {loading && conversas.length === 0 ? (
        <div className="content-card content-card--centered">
          <div className="screen-loader__spinner" />
          <p>Carregando painel...</p>
        </div>
      ) : conversas.length === 0 ? (
        <div className="empty-state">
          <strong>Tudo limpo 🎉</strong>
          <span>Nenhuma conversa em aberto no momento.</span>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          // Quadros de ~320px: cabem quantos couberem na largura e o resto
          // QUEBRA PRA BAIXO (sem scroll horizontal).
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 12, alignItems: 'start', marginTop: 4,
        }}>
          {grupos.map(([setor, lista]) => {
            const esperando = contaEsperando(lista);
            const semAt = lista.filter((c) => !c.atendente).length;
            return (
              <section key={setor} className="content-card" style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <header style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', borderBottom: '1px solid var(--portal-border)', background: 'var(--portal-surface)' }}>
                  <i className="bi bi-inboxes" style={{ color: '#3D8B37' }} />
                  <strong style={{ fontSize: '0.92rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{setor}</strong>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, background: 'rgba(61,139,55,0.14)', color: '#3D8B37', borderRadius: 10, padding: '1px 8px' }}>{lista.length}</span>
                </header>
                {(esperando > 0 || semAt > 0) && (
                  <div style={{ display: 'flex', gap: 10, padding: '5px 12px', fontSize: '0.7rem', borderBottom: '1px solid var(--portal-border)' }}>
                    {esperando > 0 && <span style={{ fontWeight: 700, color: '#F5A623' }}>{esperando} esperando</span>}
                    {semAt > 0 && <span style={{ fontWeight: 700, color: '#b43737' }}>{semAt} sem atendente</span>}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10 }}>
                  {lista.map((c) => (
                    <CardConversa key={c.id} c={c} agora={agora} onEntrar={entrar} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
